/**
 * Hooks for spectator mode: playback of a single source, and the unified
 * list of watchable sources (Live Java per-player + Neo tables).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { BACKEND_URL } from '../config';

export type SpectatorMode = 'live' | 'neo_libre' | 'neo_compet';
export type SpectatorSourceType = 'neo' | 'java_live';

export interface SpectatorEvent {
  seq: number;
  serverTs: number;
  dealNumber: number;
  type: string;
  payload: Record<string, unknown>;
}

export interface SpectatorPhasePreview {
  phase: 'attente' | 'enchere' | 'chien' | 'ecart' | 'jeu' | 'score';
  dealNumber: number;
  trickNumber?: number;
  lastTrick?: Array<{ player: number; card: number }>;
}

/** Best-effort progress/performance snapshot for a deal — see backend spectator-live-progress.ts. */
export interface SpectatorLiveProgress {
  dealNumber: number;
  trickNumber: number;
  phase: SpectatorPhasePreview['phase'];
  taker?: number;
  takerPoints?: number;
  observedIsTaker?: boolean;
}

/** Standing of the tapped player in the Live tournament in progress. */
export interface LiveTournamentPlayerRanking {
  pseudo: string;
  etat?: string;
  clDonne?: number;
  pointsTournoi?: number;
  clTournoi?: number;
  clGeneral?: number;
  updatedAt: number;
}

export interface WatchableSourceSummary {
  sourceId: string;
  sourceType: SpectatorSourceType;
  mode: SpectatorMode;
  tableLabel: string;
  playerPseudo?: string;
  players: Array<{ pseudo: string; position: number }>;
  compet: boolean;
  currentDealNumber: number;
  playableDealNumber: number | null;
  liveDonnes?: { current: number; max: number };
  phasePreview?: SpectatorPhasePreview;
  liveProgress?: SpectatorLiveProgress;
  lastDealResult?: SpectatorLiveProgress;
  liveRanking?: LiveTournamentPlayerRanking;
  lastEventAt: number | null;
}

export interface SpectatorPlaybackState {
  sourceId: string;
  dealNumber: number;
  status: 'ready' | 'waiting' | 'live';
  realtime: boolean;
  message?: string;
  players: Array<{ pseudo: string; position: number }>;
  totalEvents: number;
}

export interface SpectatorChatMessage {
  from?: string;
  message?: string;
  timestamp: number;
}

const SOURCES_POLL_INTERVAL_MS = 15000;

/**
 * Unified list of every watchable game, optionally filtered server-side.
 * Kept fresh via REST polling (fallback) + `spectator:sources_updated` push.
 */
export function useSpectatorSources(
  socket: Socket | null,
  mode: SpectatorMode | 'all' = 'all',
  pseudo?: string,
  enabled = true,
) {
  const [sources, setSources] = useState<WatchableSourceSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSources = useCallback(async () => {
    if (!enabled) return;
    try {
      const params = new URLSearchParams();
      if (mode !== 'all') params.set('mode', mode);
      // Temporaire (multiplex réservé aux admins pendant la bêta) : voir spectator-access.ts.
      if (pseudo) params.set('pseudo', pseudo);
      const qs = params.toString();
      const res = await fetch(`${BACKEND_URL}/api/spectator/sources${qs ? `?${qs}` : ''}`);
      if (res.ok) {
        const data = await res.json();
        setSources(data.sources ?? []);
      }
    } catch (err) {
      console.error('[useSpectatorSources] fetch error', err);
    } finally {
      setLoading(false);
    }
  }, [mode, pseudo, enabled]);

  useEffect(() => {
    if (!enabled) return;
    fetchSources();
    const interval = setInterval(fetchSources, SOURCES_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchSources, enabled]);

  useEffect(() => {
    if (!socket || !enabled) return;

    const onSourcesUpdated = (data: { sources: WatchableSourceSummary[] }) => {
      setSources(data.sources ?? []);
    };

    socket.on('spectator:sources_updated', onSourcesUpdated);
    socket.emit('spectator:get_sources', { mode, pseudo });

    return () => {
      socket.off('spectator:sources_updated', onSourcesUpdated);
    };
  }, [socket, mode, pseudo, enabled]);

  const filtered = mode === 'all' ? sources : sources.filter(s => s.mode === mode);

  return { sources: filtered, loading, refetch: fetchSources };
}

export function useSpectatorPlayback(socket: Socket | null, sourceId: string | null) {
  const [playbackState, setPlaybackState] = useState<SpectatorPlaybackState | null>(null);
  const [events, setEvents] = useState<SpectatorEvent[]>([]);
  const [chatMessages, setChatMessages] = useState<SpectatorChatMessage[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const joinedRef = useRef(false);

  const join = useCallback((opts?: { pseudo?: string; realtime?: boolean; hasCompetitionAccess?: boolean }) => {
    if (!socket || !sourceId || joinedRef.current) return;
    joinedRef.current = true;
    socket.emit('spectator:join', {
      sourceId,
      pseudo: opts?.pseudo,
      realtime: opts?.realtime,
      hasCompetitionAccess: opts?.hasCompetitionAccess,
    });
  }, [socket, sourceId]);

  const leave = useCallback(() => {
    if (!socket || !sourceId) return;
    socket.emit('spectator:leave', { sourceId });
    joinedRef.current = false;
    setEvents([]);
    setChatMessages([]);
    setPlaybackState(null);
    setIsComplete(false);
  }, [socket, sourceId]);

  const pause = useCallback(() => {
    if (!socket || !sourceId) return;
    socket.emit('spectator:pause', { sourceId });
  }, [socket, sourceId]);

  const play = useCallback(() => {
    if (!socket || !sourceId) return;
    socket.emit('spectator:play', { sourceId });
  }, [socket, sourceId]);

  const setSpeed = useCallback((speed: number) => {
    if (!socket || !sourceId) return;
    socket.emit('spectator:set_speed', { sourceId, speed });
  }, [socket, sourceId]);

  useEffect(() => {
    if (!socket) return;

    const onState = (data: SpectatorPlaybackState) => {
      if (sourceId && data.sourceId !== sourceId) return;
      setPlaybackState(data);
      setError(null);
    };

    const onEvent = (data: { event: SpectatorEvent }) => {
      setEvents(prev => [...prev, data.event]);
    };

    const onSnapshot = (data: { events: SpectatorEvent[] }) => {
      setEvents(data.events);
    };

    const onChat = (data: SpectatorChatMessage) => {
      setChatMessages(prev => [...prev, data]);
    };

    const onComplete = () => setIsComplete(true);

    const onDealUpdated = (data: { status?: string; message?: string; dealNumber?: number }) => {
      if (data.status === 'ready') {
        setIsComplete(false);
        setEvents([]);
        setChatMessages([]);
      }
      if (data.message) {
        setPlaybackState(prev => prev ? { ...prev, message: data.message, status: (data.status as SpectatorPlaybackState['status']) ?? prev.status } : prev);
      }
    };

    const onError = (data: { message?: string }) => {
      setError(data.message ?? 'Erreur spectateur');
    };

    socket.on('spectator:playback_state', onState);
    socket.on('spectator:playback_event', onEvent);
    socket.on('spectator:playback_snapshot', onSnapshot);
    socket.on('spectator:chat', onChat);
    socket.on('spectator:playback_complete', onComplete);
    socket.on('spectator:deal_updated', onDealUpdated);
    socket.on('spectator:error', onError);

    return () => {
      socket.off('spectator:playback_state', onState);
      socket.off('spectator:playback_event', onEvent);
      socket.off('spectator:playback_snapshot', onSnapshot);
      socket.off('spectator:chat', onChat);
      socket.off('spectator:playback_complete', onComplete);
      socket.off('spectator:deal_updated', onDealUpdated);
      socket.off('spectator:error', onError);
    };
  }, [socket, sourceId]);

  useEffect(() => {
    return () => {
      if (joinedRef.current) leave();
    };
  }, [leave]);

  return {
    playbackState,
    events,
    chatMessages,
    isComplete,
    error,
    join,
    leave,
    pause,
    play,
    setSpeed,
  };
}
